import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Loader2, CheckCircle2, PhoneCall, ExternalLink, AlertCircle, Pencil } from 'lucide-react';
import styles from './PublicFeedback.module.css';

/**
 * The feedback form a client opens from WhatsApp: /f/:token (no login).
 * Stars first; each rating then asks its own question with its own options (from the salon's settings).
 * Low ratings can ask for a call back; a top rating can go straight to the salon's Google review page.
 */
const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').replace(/\/api\/v1(?:\/public)?\/?$/, '').replace(/\/+$/, '');
const API = `${API_ORIGIN}/api/v1/public/feedback`;
const EMOJI = { 1: '😞', 2: '😕', 3: '🙂', 4: '😊', 5: '😍' };
const CALL_TIMES = ['Morning', 'Afternoon', 'Evening', 'Any time'];
const REDIRECT_SECONDS = 4;

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function PublicFeedback() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [tags, setTags] = useState([]);
    const [comment, setComment] = useState('');
    const [wantsCallback, setWantsCallback] = useState(false);
    const [callbackTime, setCallbackTime] = useState('');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState('');
    const [done, setDone] = useState(null);           // { rating, thankYou, google }
    const [editing, setEditing] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const timer = useRef(null);
    const detailsRef = useRef(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${API}/${token}`);
                const j = await res.json().catch(() => ({}));
                if (!alive) return;
                if (!res.ok || !j.success) { setError(j.message || 'This feedback link is not valid.'); return; }
                setData(j.data);
                if (j.data.answer) {
                    setRating(j.data.answer.rating);
                    setTags(j.data.answer.tags || []);
                    setComment(j.data.answer.comment || '');
                    setWantsCallback(Boolean(j.data.answer.wantsCallback));
                    setCallbackTime(j.data.answer.callbackTime || '');
                }
            } catch (e) {
                if (alive) setError('Could not open the form. Check your internet and try again.');
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; clearInterval(timer.current); };
    }, [token]);

    const form = data?.form;
    const shown = hover || rating;
    const askCall = rating > 0 && form && rating < form.callbackBelow;
    const chips = useMemo(() => (form && rating ? form.chips[rating] || [] : []), [form, rating]);

    const pick = (n) => {
        if (n !== rating) setTags([]);            // the options change with the rating
        setRating(n);
        if (!(form && n < form.callbackBelow)) setWantsCallback(false);
        setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    };
    const toggleTag = (t) => setTags((list) => (list.includes(t) ? list.filter((x) => x !== t) : [...list, t]));

    const goGoogle = async (url) => {
        clearInterval(timer.current);
        setCountdown(0);
        fetch(`${API}/${token}/google`, { method: 'POST' }).catch(() => {});
        window.location.href = url;
    };

    const submit = async () => {
        if (!rating || sending) return;
        setSending(true); setSendError('');
        try {
            const res = await fetch(`${API}/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rating, tags, comment: comment.trim(), wantsCallback: askCall && wantsCallback, callbackTime: askCall && wantsCallback ? callbackTime : '' }),
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) { setSendError(j.message || 'Could not send. Please try again.'); return; }
            setDone(j.data);
            setEditing(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // top rating + Google link: off to Google in a few seconds (can be stopped)
            if (j.data.google?.url && j.data.google.auto) {
                setCountdown(REDIRECT_SECONDS);
                timer.current = setInterval(() => {
                    setCountdown((c) => {
                        if (c <= 1) { clearInterval(timer.current); goGoogle(j.data.google.url); return 0; }
                        return c - 1;
                    });
                }, 1000);
            }
        } catch (e) {
            setSendError('Could not send. Check your internet and try again.');
        } finally { setSending(false); }
    };

    /* ---------------- states ---------------- */
    if (loading) return <div className={styles.center}><Loader2 size={26} className={styles.spin} /></div>;
    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.card} style={{ textAlign: 'center' }}>
                    <AlertCircle size={34} className={styles.muted} />
                    <h1 className={styles.title} style={{ marginTop: 10 }}>Feedback link</h1>
                    <p className={styles.sub}>{error}</p>
                </div>
            </div>
        );
    }

    const Header = (
        <header className={styles.brand}>
            {data.salon.logo ? <img src={data.salon.logo} alt="" className={styles.logo} /> : <span className={styles.logoFallback}>{String(data.salon.name || 'S').charAt(0)}</span>}
            <div>
                <strong>{data.salon.name}</strong>
                {data.salon.address && <small>{data.salon.address}</small>}
            </div>
        </header>
    );

    /* ---------------- thank you ---------------- */
    const answered = done || (data.submitted && !editing ? { rating: data.answer.rating, thankYou: 'Thank you, your feedback is saved.', google: null } : null);
    if (answered) {
        return (
            <div className={styles.page}>
                {Header}
                <div className={`${styles.card} ${styles.thanks}`}>
                    <div className={styles.bigEmoji} aria-hidden="true">{EMOJI[answered.rating]}</div>
                    <div className={styles.starsSmall} aria-label={`${answered.rating} out of 5`}>
                        {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={20} className={n <= answered.rating ? styles.on : styles.off} />)}
                    </div>
                    <h1 className={styles.title}>Thank you{data.client.firstName ? `, ${data.client.firstName}` : ''}!</h1>
                    <p className={styles.sub}>{answered.thankYou}</p>

                    {answered.google?.url && (
                        <div className={styles.google}>
                            <strong>Would you share it on Google?</strong>
                            <span>It takes a minute and helps {data.salon.name} a lot. 💜</span>
                            <button type="button" className={styles.googleBtn} onClick={() => goGoogle(answered.google.url)}>
                                <ExternalLink size={16} /> Write a Google review
                            </button>
                            {countdown > 0 && (
                                <small className={styles.countdown}>
                                    Opening Google in {countdown}s…{' '}
                                    <button type="button" onClick={() => { clearInterval(timer.current); setCountdown(0); }}>Stay here</button>
                                </small>
                            )}
                        </div>
                    )}

                    {(done ? true : data.canEdit) && (
                        <button type="button" className={styles.linkBtn} onClick={() => { clearInterval(timer.current); setCountdown(0); setDone(null); setEditing(true); }}>
                            <Pencil size={13} /> Change my feedback
                        </button>
                    )}
                </div>
                <p className={styles.foot}>Powered by Hirsta</p>
            </div>
        );
    }

    /* ---------------- form ---------------- */
    return (
        <div className={styles.page}>
            {Header}
            <div className={styles.card}>
                <h1 className={styles.title}>Hi {data.client.firstName || 'there'} 👋</h1>
                <p className={styles.sub}>How was your visit{data.visit?.at ? ` on ${fmtDay(data.visit.at)}` : ''}?</p>
                {data.visit?.services?.length > 0 && (
                    <div className={styles.visit}>{data.visit.services.slice(0, 4).join(' · ')}{data.visit.services.length > 4 ? ` +${data.visit.services.length - 4}` : ''}</div>
                )}

                <div className={styles.stars} role="radiogroup" aria-label="Your rating" onMouseLeave={() => setHover(0)}>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={rating === n}
                            aria-label={`${n} star${n === 1 ? '' : 's'}: ${form.labels[n]}`}
                            className={styles.starBtn}
                            onMouseEnter={() => setHover(n)}
                            onFocus={() => setHover(n)}
                            onBlur={() => setHover(0)}
                            onClick={() => pick(n)}
                        >
                            <Star size={42} className={n <= shown ? styles.on : styles.off} />
                        </button>
                    ))}
                </div>
                <div className={styles.label} aria-live="polite">{shown ? `${EMOJI[shown]} ${form.labels[shown]}` : 'Tap a star'}</div>
            </div>

            {rating > 0 && (
                <div className={styles.card} ref={detailsRef}>
                    <h2 className={styles.question}>{form.questions[rating]}</h2>
                    {chips.length > 0 && (
                        <div className={styles.chips} role="group" aria-label="Choose what applies">
                            {chips.map((t) => (
                                <button type="button" key={t} aria-pressed={tags.includes(t)} className={`${styles.chip} ${tags.includes(t) ? (rating >= 4 ? styles.chipGood : styles.chipBad) : ''}`} onClick={() => toggleTag(t)}>
                                    {tags.includes(t) && <CheckCircle2 size={14} />} {t}
                                </button>
                            ))}
                        </div>
                    )}
                    <textarea
                        className={styles.comment}
                        rows={3}
                        maxLength={1000}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={rating >= 4 ? 'Anything else you would like to say? (optional)' : 'Tell us a little more (optional)'}
                        aria-label="Your comment"
                    />

                    {askCall && (
                        <div className={styles.callBox}>
                            <label className={styles.callToggle}>
                                <input type="checkbox" checked={wantsCallback} onChange={(e) => setWantsCallback(e.target.checked)} />
                                <span><PhoneCall size={15} /> Yes, please call me to sort this out</span>
                            </label>
                            {wantsCallback && (
                                <div className={styles.times} role="radiogroup" aria-label="Best time to call">
                                    {CALL_TIMES.map((t) => (
                                        <button type="button" key={t} role="radio" aria-checked={callbackTime === t} className={`${styles.time} ${callbackTime === t ? styles.timeOn : ''}`} onClick={() => setCallbackTime(t)}>{t}</button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {sendError && <p className={styles.error} role="alert">{sendError}</p>}
                    <button type="button" className={styles.submit} onClick={submit} disabled={sending}>
                        {sending ? <Loader2 size={18} className={styles.spin} /> : null} {editing ? 'Update feedback' : 'Send feedback'}
                    </button>
                </div>
            )}
            <p className={styles.foot}>Your feedback goes only to {data.salon.name}. Powered by Hirsta</p>
        </div>
    );
}
